import { BlogSpecialtyRelation } from 'types/api/blog';

/**
 * The blog detail response has not been pinned down for the
 * specialties relation — it may come back as relation objects or as
 * bare ids, and the `type` discriminator may or may not be present.
 * Normalising here keeps that uncertainty out of the form.
 */
export type RawRelation = BlogSpecialtyRelation | number | null | undefined;

const toId = (item: RawRelation): number | null => {
  if (typeof item === 'number') return item;

  if (item && typeof item === 'object' && typeof item.id === 'number') {
    return item.id;
  }

  return null;
};

/**
 * Split the blog's attached specialty relations into the two pickers.
 *
 * `type` is used when the BE sends it; otherwise membership of the
 * symptom half of the catalog decides. Ids in neither bucket fall
 * through to `specialties` so they stay **visible and removable** in the
 * form rather than being silently dropped (and then wiped on save, since
 * the payload replaces the whole relation set).
 */
export const splitSpecialtyRelations = (
  attached: RawRelation[] | null | undefined,
  symptomCatalogIds: Set<number>
): { symptoms: number[]; specialties: number[] } => {
  const symptoms: number[] = [];
  const specialties: number[] = [];

  for (const item of attached || []) {
    const id = toId(item);
    if (id === null) continue;

    const type = typeof item === 'object' ? item?.type : undefined;

    const isSymptom =
      type === 'symptom' || (type === undefined && symptomCatalogIds.has(id));

    if (isSymptom) {
      symptoms.push(id);
    } else {
      specialties.push(id);
    }
  }

  return { symptoms, specialties };
};
